import { useState, useRef, useEffect } from "react";
import { api, CATEGORIES_EXPENSE, compressImage } from "../lib/api";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import DocumentAnalysisProgress, { useDocumentAnalysisProgress } from "../components/DocumentAnalysisProgress";
import MortgageBreakdownCard from "../components/MortgageBreakdownCard";
import { Upload, ScanLine, Sparkles, CheckCircle2, ListChecks, Plus, Trash2, Loader2, FileText, ExternalLink } from "lucide-react";

import { toast } from "sonner";
import { Field, SelectField } from "./Properties";

const normalizeMerchant = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/^(the|a|an)\s+/i, "")
    .replace(/[^a-z0-9]/g, "");

const findMatchingVendor = (name, list) => {
  if (!name) return null;
  const normD = normalizeMerchant(name);
  if (!normD) return null;
  return list.find((v) => {
    const normV = normalizeMerchant(v.name);
    return normV === normD || normV.includes(normD) || normD.includes(normV);
  });
};

export default function ReceiptScanner() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const {
    progress: aiScanProgress,
    status: aiScanStatus,
    stepText: aiScanStepText,
    startProgress: startAiScanProgress,
    completeProgress: completeAiScanProgress,
    resetProgress: resetAiScanProgress,
  } = useDocumentAnalysisProgress();
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(null);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [isItemized, setIsItemized] = useState(false);
  const [originalTotal, setOriginalTotal] = useState("");
  const [detectedVendor, setDetectedVendor] = useState("");
  const [showQuickAddVendor, setShowQuickAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [creatingVendor, setCreatingVendor] = useState(false);
  const inputRef = useRef();


  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
  }, []);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setForm(null);
    setItems([]);
    setIsItemized(false);
    resetAiScanProgress();
  };

  const scan = async () => {
    if (!file) return;
    setScanning(true);
    startAiScanProgress(file.name);
    try {
      const uploadFile = await compressImage(file);
      const fd = new FormData();
      fd.append("file", uploadFile);
      const r = await api.post("/ai/extract-receipt", fd, {
        timeout: 90000,
      });
      setResult(r.data);
      const extractedItems = (r.data.items || []).map((it, idx) => ({
        id: idx + 1,
        description: it.description || `Item ${idx + 1}`,
        amount: it.amount != null ? it.amount.toString() : "0.00",
        selected: true,
      }));
      setItems(extractedItems);
      setOriginalTotal(r.data.total_amount != null ? r.data.total_amount.toString() : "");
      setIsItemized(false);

      // Match vendor if exists
      let matchedVendorId = "";
      if (r.data.vendor) {
        setDetectedVendor(r.data.vendor);
        const matched = findMatchingVendor(r.data.vendor, vendors);
        if (matched) {
          matchedVendorId = matched.id;
          toast.info(`Matched merchant to existing vendor: "${matched.name}"`);
        }
      } else {
        setDetectedVendor("");
      }

      // Match property if detected
      let matchedPropertyId = r.data.matched_property_id || "";
      if (!matchedPropertyId && (r.data.property_address || r.data.vendor)) {
        const queryText = `${r.data.property_address || ""} ${r.data.notes || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
        const foundProp = properties.find((p) => {
          const pAddr = (p.address || "").toLowerCase().replace(/[^a-z0-9]/g, "");
          const pName = (p.name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
          return (pAddr && queryText.includes(pAddr)) || (pName && queryText.includes(pName));
        });
        if (foundProp) {
          matchedPropertyId = foundProp.id;
        }
      }
      if (matchedPropertyId) {
        const pObj = properties.find((p) => p.id === matchedPropertyId);
        if (pObj) {
          toast.info(`Matched statement to property: "${pObj.name}"`);
        }
      }

      const isMortgage =
        (r.data.suggested_category && r.data.suggested_category.toLowerCase().includes("mortgage")) ||
        (r.data.vendor && r.data.vendor.toLowerCase().includes("mortgage")) ||
        (file?.name && file.name.toLowerCase().includes("mortgage")) ||
        (r.data.principal_amount != null && r.data.principal_amount > 0);

      const resolvedCategory = r.data.suggested_category
        ? r.data.suggested_category
        : (isMortgage ? "Mortgage Interest" : "Other");

      setForm({
        date: r.data.date || new Date().toISOString().slice(0, 10),
        amount: r.data.total_amount?.toString() || "",
        type: "Expense",
        category: resolvedCategory,
        property_id: matchedPropertyId,
        vendor_id: matchedVendorId,
        notes: r.data.vendor
          ? (isMortgage ? `Mortgage: ${r.data.vendor} (P&I + Escrow)` : `Receipt: ${r.data.vendor}`)
          : "",
        receipt_path: r.data.receipt_path || "",
        deductible_pct: r.data.deductible_pct != null ? r.data.deductible_pct : (isMortgage ? 91.2 : 100),
        deductible_amount: r.data.deductible_amount != null ? r.data.deductible_amount.toString() : "",
        principal_amount: r.data.principal_amount || 0,
        interest_amount: r.data.interest_amount || 0,
        escrow_amount: r.data.escrow_amount || 0,
        deductibility_reason: r.data.deductibility_reason || (isMortgage ? "Principal is non-deductible; Interest + Escrow is Schedule E deductible." : ""),
      });
      completeAiScanProgress();
      await new Promise((resolve) => setTimeout(resolve, 400));
      if (r.data.total_amount == null && r.data.raw) {
        toast.warning(r.data.raw.slice(0, 160));
      } else {
        toast.success(`Receipt analyzed (${extractedItems.length} items detected)`);
      }
    } catch (e) {
      resetAiScanProgress();
      if (e.code === "ECONNABORTED" || e.message?.includes("timeout")) {
        toast.error("Analysis timed out. Please try again with a clearer image or check Gemini API status.");
      } else {
        toast.error(e.response?.data?.detail || "Failed to scan receipt");
      }
    } finally {
      setScanning(false);
    }
  };

  // Quick add vendor handler
  const handleQuickAddVendor = async (nameToCreate) => {
    const name = (nameToCreate || newVendorName).trim();
    if (!name) return;
    setCreatingVendor(true);
    try {
      const res = await api.post("/vendors", {
        name,
        category: form?.category || "Repairs",
      });
      const newV = res.data;
      setVendors((prev) => [...prev, newV]);
      setForm((prev) => ({ ...prev, vendor_id: newV.id }));
      setShowQuickAddVendor(false);
      setNewVendorName("");
      toast.success(`Vendor "${name}" created and selected!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create vendor");
    } finally {
      setCreatingVendor(false);
    }
  };


  const toggleItemize = (enabled) => {
    setIsItemized(enabled);
    if (enabled) {
      const sumSelected = items
        .filter((i) => i.selected)
        .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
      setForm((prev) => ({
        ...prev,
        amount: sumSelected > 0 ? sumSelected.toFixed(2) : prev.amount,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        amount: originalTotal || prev.amount,
      }));
    }
  };

  const toggleItemCheck = (idx, checked) => {
    const updated = items.map((it, i) => (i === idx ? { ...it, selected: checked } : it));
    setItems(updated);
    if (isItemized) {
      const sumSelected = updated
        .filter((i) => i.selected)
        .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
      setForm((prev) => ({ ...prev, amount: sumSelected.toFixed(2) }));
    }
  };

  const updateItemField = (idx, field, val) => {
    const updated = items.map((it, i) => (i === idx ? { ...it, [field]: val } : it));
    setItems(updated);
    if (isItemized && field === "amount") {
      const sumSelected = updated
        .filter((i) => i.selected)
        .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
      setForm((prev) => ({ ...prev, amount: sumSelected.toFixed(2) }));
    }
  };

  const deleteItem = (idx) => {
    const updated = items.filter((_, i) => i !== idx);
    setItems(updated);
    if (isItemized) {
      const sumSelected = updated
        .filter((i) => i.selected)
        .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
      setForm((prev) => ({ ...prev, amount: sumSelected.toFixed(2) }));
    }
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      description: "New Item",
      amount: "0.00",
      selected: true,
    };
    const updated = [...items, newItem];
    setItems(updated);
  };

  const deductibleTotal = items
    .filter((i) => i.selected)
    .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const excludedTotal = items
    .filter((i) => !i.selected)
    .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

  const save = async (e) => {
    e.preventDefault();
    try {
      let finalNotes = form.notes || "";
      if (isItemized && excludedTotal > 0) {
        finalNotes = finalNotes
          ? `${finalNotes} · (Excluded $${excludedTotal.toFixed(2)} personal items)`
          : `Excluded $${excludedTotal.toFixed(2)} personal items`;
      }
      const amtVal = parseFloat(form.amount) || 0;
      const dedPctVal = form.deductible_pct != null ? parseFloat(form.deductible_pct) : 100;
      const dedAmtVal = form.deductible_amount !== "" && form.deductible_amount != null
        ? parseFloat(form.deductible_amount)
        : Number((amtVal * (dedPctVal / 100)).toFixed(2));

      const payload = {
        ...form,
        amount: amtVal,
        deductible_pct: dedPctVal,
        deductible_amount: dedAmtVal,
        principal_amount: parseFloat(form.principal_amount) || 0,
        interest_amount: parseFloat(form.interest_amount) || 0,
        escrow_amount: parseFloat(form.escrow_amount) || 0,
        deductibility_reason: form.deductibility_reason || null,
        notes: finalNotes,
        property_id: form.property_id || null,
        vendor_id: form.vendor_id || null,
        receipt_items: isItemized ? JSON.stringify(items) : null,
      };
      await api.post("/transactions", payload);
      toast.success("Transaction saved");
      // Reset
      setFile(null); setPreview(null); setResult(null); setForm(null); setItems([]); setIsItemized(false);
      resetAiScanProgress();
    } catch (e) {
      toast.error("Failed to save");
    }
  };


  return (
    <Layout title="Receipt Scanner" subtitle="AI-powered OCR with category suggestion">
      {!preview && (
        <button
          onClick={() => inputRef.current?.click()}
          data-testid="upload-zone"
          className="w-full rounded-3xl bg-[#F5F5F7]/50 hover:bg-[#1C3A5E]/5 transition-colors border-2 border-dashed border-[#1C3A5E]/20 text-[#1C3A5E] flex flex-col items-center justify-center p-12 sm:p-16 text-center cursor-pointer group"
        >
          <div className="w-16 h-16 rounded-full bg-[#1C3A5E]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <ScanLine size={32} />
          </div>
          <div className="text-lg font-bold">Drop or pick a receipt</div>
          <div className="text-sm text-[#86868B] mt-1">JPG, PNG, or WEBP. Camera capture requires HTTPS.</div>
          <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#1C3A5E]">
            <Sparkles size={14} />
            <span>Powered by Gemini</span>
            <HelpTip topic="receipt-scanner" />
          </div>

        </button>
      )}

      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        ref={inputRef}
        onChange={(e) => pick(e.target.files?.[0])}
        className="hidden"
        capture="environment"
        data-testid="receipt-file-input"
      />

      {preview && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card overflow-hidden relative">
            {file?.type === "application/pdf" || file?.name?.toLowerCase().endsWith(".pdf") ? (
              <div className="p-8 flex flex-col items-center justify-center gap-3 bg-[var(--surface-hover)] min-h-[260px] text-center">
                <FileText size={48} className="text-teal-400" />
                <div className="font-bold text-sm text-[var(--text)]">{file.name}</div>
                <div className="text-xs text-[var(--muted)]">PDF Document ({(file.size / 1024).toFixed(0)} KB)</div>
                <a
                  href={preview}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 mt-2"
                >
                  <ExternalLink size={14} />
                  <span>Open PDF in New Tab</span>
                </a>
              </div>
            ) : (
              <img src={preview} alt="receipt" className="w-full max-h-[480px] object-contain bg-[#F5F5F7]" data-testid="receipt-preview" />
            )}

            {scanning && (
              <DocumentAnalysisProgress
                progress={aiScanProgress}
                status={aiScanStatus}
                stepText={aiScanStepText}
                docName={file?.name}
                isOverlay={true}
              />
            )}

            <div className="p-4 flex gap-3">
              {!result && (
                <button onClick={scan} disabled={scanning} data-testid="scan-button" className="btn-primary flex items-center gap-2">
                  <Sparkles size={16} /> {scanning ? `Analyzing (${Math.round(aiScanProgress)}%)` : "Analyze with AI"}
                </button>
              )}
              <button onClick={() => { setFile(null); setPreview(null); setResult(null); setForm(null); resetAiScanProgress(); }}
                disabled={scanning}
                data-testid="reset-scan-button" className="px-4 py-2 text-[#86868B] text-sm font-semibold disabled:opacity-50">
                Choose another
              </button>
            </div>
          </div>

          {form && (
            <form onSubmit={save} className="card p-6 space-y-4" data-testid="scan-result-form">
              <div className="flex items-center gap-2 text-[#1f8e3a]">
                <CheckCircle2 size={18} />
                <span className="text-sm font-semibold">Review and save</span>
              </div>
              <Field label="Vendor / Notes" value={form.notes} onChange={(v) => setForm({...form, notes: v})} testId="scan-notes" />
              <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({...form, date: v})} required testId="scan-date" />
              <Field label="Amount" type="number" value={form.amount} onChange={(v) => setForm({...form, amount: v})} required testId="scan-amount" />
              <SelectField label="IRS Category" value={form.category} onChange={(v) => setForm({...form, category: v})} options={CATEGORIES_EXPENSE} testId="scan-category" />

              {/* Mortgage Statement Auto-Split & Schedule E Deductibility Card */}
              {(form.category === "Mortgage Interest" || form.principal_amount > 0) && (
                <MortgageBreakdownCard
                  amount={form.amount}
                  principalAmount={form.principal_amount}
                  interestAmount={form.interest_amount}
                  escrowAmount={form.escrow_amount}
                  deductibleAmount={form.deductible_amount}
                  deductiblePct={form.deductible_pct}
                  onChange={(fields) => setForm((prev) => ({ ...prev, ...fields }))}
                />
              )}
              <div>
                <label className="label">Property</label>
                <select className="lapen-input" value={form.property_id} onChange={(e) => setForm({...form, property_id: e.target.value})} data-testid="scan-property">
                  <option value="">— None —</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="label mb-0">Vendor / Payee (optional)</label>
                  <button
                    type="button"
                    onClick={() => setShowQuickAddVendor(!showQuickAddVendor)}
                    className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1 font-medium"
                  >
                    <Plus size={13} /> {showQuickAddVendor ? "Cancel" : "+ New Vendor"}
                  </button>
                </div>

                <select className="lapen-input" value={form.vendor_id} onChange={(e) => setForm({...form, vendor_id: e.target.value})} data-testid="scan-vendor">
                  <option value="">— None —</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.category || "Vendor"})</option>)}
                </select>

                {/* Smart Detected Merchant from Receipt */}
                {detectedVendor && (
                  <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/30 text-xs flex items-center justify-between gap-2">
                    <div>
                      <span className="font-semibold text-teal-400 flex items-center gap-1">
                        <Sparkles size={13} /> Detected merchant:
                      </span>
                      <span className="font-bold text-[var(--text)]">"{detectedVendor}"</span>
                      {vendors.some(v => v.id === form.vendor_id) ? (
                        <span className="text-[11px] text-[var(--muted)] block">
                          Assigned to: <strong>{vendors.find(v => v.id === form.vendor_id)?.name}</strong>
                        </span>
                      ) : (
                        <span className="text-[11px] text-[var(--muted)] block">Not in your vendor list yet.</span>
                      )}
                    </div>
                    {!form.vendor_id && (
                      <button
                        type="button"
                        onClick={() => handleQuickAddVendor(detectedVendor)}
                        disabled={creatingVendor}
                        className="btn-primary text-xs py-1 px-2.5 shrink-0 flex items-center gap-1 shadow-sm"
                      >
                        {creatingVendor ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                        <span>Add "{detectedVendor}"</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Quick Add Vendor Inline */}
                {showQuickAddVendor && (
                  <div className="p-3 rounded-xl border border-[var(--border)] bg-[var(--surface-hover)] space-y-2 mt-2">
                    <div className="text-xs font-semibold text-[var(--text)]">Create New Vendor</div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="lapen-input text-xs py-1.5 flex-1"
                        placeholder="Merchant / Vendor name"
                        value={newVendorName}
                        onChange={(e) => setNewVendorName(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => handleQuickAddVendor(newVendorName)}
                        disabled={creatingVendor || !newVendorName.trim()}
                        className="btn-primary text-xs py-1.5 px-3 shrink-0 flex items-center gap-1"
                      >
                        {creatingVendor && <Loader2 size={12} className="animate-spin" />}
                        <span>Add Vendor</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>


              {/* Itemize Receipt Toggle */}
              <div className="rounded-2xl border border-[#E5E5EA] p-4 bg-[#F9F9FB] space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-[#007AFF]/10 text-[#007AFF] flex items-center justify-center shrink-0">
                      <ListChecks size={18} />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#1C3A5E]">Itemize Receipt</div>
                      <div className="text-xs text-[#86868B]">
                        {items.length > 0
                          ? `${items.length} line items detected. Uncheck any personal items.`
                          : "Breakdown into items and exclude personal purchases"}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleItemize(!isItemized)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      isItemized ? "bg-[#34C759]" : "bg-[#D1D1D6]"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        isItemized ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {isItemized && (
                  <div className="pt-3 border-t border-[#E5E5EA] space-y-2.5">
                    <div className="flex items-center justify-between text-xs font-semibold text-[#86868B]">
                      <span>Items ({items.filter((i) => i.selected).length} of {items.length} included)</span>
                      <button
                        type="button"
                        onClick={addItem}
                        className="text-[#007AFF] hover:underline flex items-center gap-1 font-medium"
                      >
                        <Plus size={13} /> Add item
                      </button>
                    </div>

                    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                      {items.map((it, idx) => (
                        <div
                          key={it.id || idx}
                          className={`flex items-center gap-2 p-2 rounded-xl border text-xs transition-colors ${
                            it.selected
                              ? "bg-white border-[#E5E5EA]"
                              : "bg-[#F2F2F7] border-dashed border-[#D1D1D6] opacity-60"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={it.selected}
                            onChange={(e) => toggleItemCheck(idx, e.target.checked)}
                            className="w-4 h-4 rounded text-[#007AFF] accent-[#007AFF] shrink-0"
                            title={it.selected ? "Deductible business expense" : "Excluded personal item"}
                          />
                          <input
                            type="text"
                            value={it.description}
                            onChange={(e) => updateItemField(idx, "description", e.target.value)}
                            placeholder="Item name"
                            className={`flex-1 min-w-0 bg-transparent font-medium focus:outline-none ${
                              !it.selected ? "line-through text-[#86868B]" : "text-[#1C3A5E]"
                            }`}
                          />
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-[#86868B]">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={it.amount}
                              onChange={(e) => updateItemField(idx, "amount", e.target.value)}
                              className="w-16 bg-transparent font-bold text-right focus:outline-none"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteItem(idx)}
                            className="p-1 text-[#86868B] hover:text-[#c1271d] shrink-0"
                            title="Delete item"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Breakdown pill */}
                    <div className="p-2.5 rounded-xl bg-white border border-[#E5E5EA] flex items-center justify-between text-xs">
                      <div>
                        <span className="text-[#86868B]">Deductible Total: </span>
                        <span className="font-bold text-[#1f8e3a]">${deductibleTotal.toFixed(2)}</span>
                      </div>
                      {excludedTotal > 0 && (
                        <div className="text-[#c1271d] font-semibold text-[11px]">
                          Excluded: -${excludedTotal.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button type="submit" className="btn-primary w-full" data-testid="scan-save-button">Save transaction</button>

            </form>
          )}
        </div>
      )}

      {result && !form && result.raw && (
        <div className="card p-4 mt-4 text-xs font-mono text-[#86868B] whitespace-pre-wrap" data-testid="scan-raw">
          {result.raw}
        </div>
      )}
    </Layout>
  );
}
